import { useState } from "react";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Typography,
  MenuItem,
  Select,
  alpha,
  Box,
  Collapse,
} from "@mui/material";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ArrowDown2 } from "../../Icon";

// Componente para la flecha del Select
const ArrowDrop = () => {
  const theme = useTheme();
  return (
    <Box className="icon-form-eg-basic-select-container">
      <Box sx={{ borderRight: `1px solid ${theme.palette.blueGray200.main}`, height: "65%", color: "transparent" }}>.</Box>
      <Box
        paddingLeft={1}
        paddingRight={1.2}
        className="icon-form-eg-basic-select"
        sx={{ 
          cursor: 'pointer',
          height: "100%",
          display: "flex",
          alignItems: "center",
          pointerEvents: "none",
        }}
      >
        <ArrowDown2 
          color={theme.palette.secondary.main} 
          size={12} 
          style={{
            height: "100%",
            marginTop: 3
          }} 
        />
      </Box>
    </Box>
  );
};

// /**
//  * @param {Object} param0
//  * @param {string} param0.label
//  * @param {string} param0.optionLabel
// //  * @param {Object<} param0.value
//  * @param {string} param0.optionValue
//  * @param {Array<{
//  *  key: string,
//  *  label: string,
//  *  options: Array<{
//  *    label: string,
//  *    value: string
//  * }>
//  * }>} param0.sections
//  * @param {Function} param0.onChange
//  * @param {string} param0.errorText
//  * @param {boolean} param0.required
//  * @returns 
//  */
export const SectionedSelect = ({
  label,
  optionLabel = "label",
  value,
  optionValue = "value",
  sections,
  onChange,
  errorText,
  required = false,
  disabled = false,
  placeholder = "",
  isValid = undefined,
  showSuccessValidation = false,
  showHelpText = true,
  name,
  ...props
}) => {
  const theme = useTheme();
  const [t] = useTranslation("components");
  const [open, setOpen] = useState(false);

  const isValidLocal = isValid !== undefined ? isValid : !!value;

  // Determina el color del borde basado en el estado
  const getBorderColor = (isFocused) => {
    const defaultColor = theme.palette.blueGray200.main;;
    if (isValidLocal === true && showSuccessValidation) return theme.palette.success.main; // Verde si es válido y showSuccessValidation es true
    // if (isValidLocal === true) return theme.palette.success.main; // Verde si es válido y showSuccessValidation es true
    if (isValidLocal === null) return defaultColor; // Verde si es válido y showSuccessValidation es true
    if (required && !isValidLocal) return theme.palette.error.main;
    if (isValidLocal && required && isFocused && showSuccessValidation) return theme.palette.success.main; // Verde si es válido y showSuccessValidation es true
    if (isFocused) return theme.palette.primary.main; // Color primario cuando está enfocado
    return defaultColor;
  };

  // Determina el color del label basado en el estado
  const getLabelColor = (isFocused) => {
    const defaultColor = theme.palette.secondary.main;
    if (isValidLocal === true && showSuccessValidation) return theme.palette.success.main; // Verde si es válido y showSuccessValidation es true
    if (isValidLocal === null) return defaultColor; // Verde si es válido y showSuccessValidation es true
    if (required && !isValidLocal) return theme.palette.error.main;
    if (isValidLocal && required && isFocused && showSuccessValidation) return theme.palette.success.main; // Verde si es válido y showSuccessValidation es true
    if (isFocused) return theme.palette.primary.main; // Color primario cuando está enfocado
    return defaultColor;
  };

  const handleChange = (e) => {
    if (e.target.value == null) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // onChange(e);
  }
  
  const handleChangeOption = (value, section) => {
    onChange({value, section});
  }

  const handleOpen = (e) => setOpen(true);

  const handleClose = (e) => {
    if (e?.target?.classList.contains('section-label')) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setOpen(false);
  }

  return (
    <FormControl fullWidth {...props} disabled={disabled}>
      {/* InputLabel con estilos condicionales */}
      <InputLabel
        {...(!!placeholder && { shrink: true })}
        sx={{
          "&.Mui-focused": {
            color: getLabelColor(true),
            transition: "all 0.25s",
          },
          "&.MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
            transition: "all 0.25s",
            top: required ? 0 : "50%",
            left: 14,
            transform: required ? "translate(0, 40%)" : "translate(0, -50%)",
          },
          "&.MuiInputLabel-shrink:not(.Mui-focused)": {
            // backgroundColor: theme.palette.common.white,
            color: getLabelColor(false),
          },
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            "&::after": {
              content: required ? '"*"' : '""',
              marginLeft: "0.2em",
            },
          }}
        >
          {label || t("EGSelects.select_option")}
        </Typography>
      </InputLabel>

      {/* Select con estilos y opciones */}
      <Select
        fullWidth
        value={value}
        label={label || t("EGSelects.select_option")}
        {...(name && { name })}
        onChange={handleChange}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        IconComponent={ArrowDrop}
        {...(!!placeholder && { displayEmpty: true })}
        MenuProps={{
          disableScrollLock: true,
          MenuListProps: { sx: { padding: 0, borderRadius: 5 } },
          slotProps: {
            paper: {
              sx: {
                border: `1px solid ${theme.palette.blueGray200.main}`,
                borderRadius: "5px",
                boxShadow: "none",
              },
            },
          },
        }}
        sx={{
          height: "40px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: getBorderColor(false),
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.secondary.main,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: "2px",
            borderColor: getBorderColor(true),
          },
          "& .MuiSelect-select": {
            color: theme.palette.secondary.main,
          },
          '& .MuiSelect-select ~ .icon-form-eg-basic-select-container': {
            backgroundColor: "white.main",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            position: "absolute",
            right: "0px",
          },
          '& .MuiSelect-select[aria-expanded="true"] ~ .icon-form-eg-basic-select-container svg': {
            transform: "rotate(-180deg)",
            transformOrigin: "center",
          },
        }}
      >
        {!!placeholder && (
          <MenuItem value="" disabled sx={{ display: "none" }}>
            <Typography fontSize={16} color={"blueGray600"}>
              {placeholder}
            </Typography>
          </MenuItem>
        )}

        {sections && sections.length > 0 ? (
          sections.map((section, index) => section.options && section.options.length > 0 ? [
            ...(section.label ? [
              <MenuItem
                key={`section-${index}`}
                className="section-label"
                sx={{
                  color: "white.main",
                  backgroundColor: "ethicsBlue.main",
                  cursor: "default",
                  height: "24px",
                  "&:hover": {
                    // backgroundColor: "white.  main",
                  },
                  "&.Mui-selected, &.Mui-selected:hover, .Mui-selected:focus": {
                    backgroundColor: "ethicsBlue.main",
                  },
                  "&:not(.Mui-selected):hover, &:not(.Mui-selected):focus": {
                    backgroundColor: "ethicsBlue.main",
                  },
                }}
              >
                <Typography
                  className="section-label"
                  fontSize={14}
                  fontWeight={600}
                  fontStyle={"italic"}
                >
                  {section.label}
                </Typography>
              </MenuItem>,
            ] : []),
            // ...[
            //   <Collapse></Collapse>
            // ],
            ...(section.options && section.options.length > 0
              ? section.options.map((option, optionIndex) => (
                <MenuItem
                  key={`option-${index}-${optionIndex}`}
                  onClick={() => handleChangeOption(option[optionValue], section.key)}
                  value={option[optionValue]}
                  disabled={option.disabled}
                  sx={{
                    color: "blueGray600.main",
                    backgroundColor: "white.main",
                    "&.Mui-selected, &.Mui-selected:hover, .Mui-selected:focus": {
                      color: "blueGray700.main",
                      backgroundColor: alpha(theme.palette.ethicsBlue.main, 0.2),
                    },
                    "&:not(.Mui-selected):hover, &:not(.Mui-selected):focus": {
                      backgroundColor: alpha(theme.palette.ethicsBlue.main, 0.1),
                    },
                  }}
                >
                  <Typography
                    fontSize={16}
                    color={
                      (option[optionValue] === value?.value && "oxfordBlue.main") || ""
                    }
                  >
                    {option[optionLabel]}
                  </Typography>
                </MenuItem>
              ))
              : [])
          ] : [])
        ) : (
          <MenuItem value="" disabled>
            <Typography marginX="auto">
              {t("EGSelects.no_options_available")}
            </Typography>
          </MenuItem>
        )}
      </Select>

      {/* Mensaje de error si es requerido y no tiene valor */}
      {showHelpText && required && !value && (
        <FormHelperText
          sx={{ color: theme.palette.error.main, mt: 0.5, fontSize: "0.7rem" }}
        >
          {`${errorText || t("EGSelects.this_field_is_required")} *`}
        </FormHelperText>
      )}
    </FormControl>
  );
};
